import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { extractErrorDetail, useIsAdmin } from '../../../components/common';
import Button from '../../../components/mplat/Button/Button';
import { Modal } from '../../../components/mplat/Modal';
import MultiSelect from '../../../components/mplat/MultiSelect';
import Select from '../../../components/mplat/Select';
import { SdnPermission, SdnPermissionType, SdnRefType, useCreateSdnPermissionMutation, useDeleteSdnPermissionMutation, useGetSdnSubjectsQuery, useLazyGetSdnPermissionsQuery, useUpdateSdnPermissionMutation } from '../../../services/sdn';
import { StorageAccessModalProps } from '../types';
import { s3dirname } from './StorageMy';

enum ACCESS_ACTION {
    DELETE = 0,
    READ = 1,
    WRITE = 2,
}

function StorageAccessModal({ isOpen, onClose, path }: StorageAccessModalProps) {
    const isAdmin = useIsAdmin();
    //#region fetches
    const [fetchCreatePermission] = useCreateSdnPermissionMutation();
    const [fetchUpdatePermission] = useUpdateSdnPermissionMutation();
    const [fetchDeletePermission] = useDeleteSdnPermissionMutation();
    const [fetchPermissions, { data: sdnPermissions }] = useLazyGetSdnPermissionsQuery();

    const { data: subjects } = useGetSdnSubjectsQuery();
    //#endregion
    //#region states
    const [permForSets, setPermForSets] = useState(SdnPermissionType.READ);
    const [gUsersForSet, setGUsersForSet] = useState<string[]>([]);
    const [myPermissions, setMyPermissions] = useState<Record<string, SdnPermission[]>>({});
    const [isFetching, setIsFetching] = useState(false);

    const availPermissions = [
        { value: ACCESS_ACTION.READ, label: t('Can view') },
        { value: ACCESS_ACTION.WRITE, label: t('Edit') },
        { value: ACCESS_ACTION.DELETE, color: 'var(--c-red)', label: t('storage:ACCESS_DELETE') },
    ];

    const availOwnerEmails = useMemo(() => {
        const issued = myPermissions[path]?.map((v) => v.group?.id || v.user?.id) || [];

        const groups = subjects?.results.groups
            .filter((g) => !issued.includes(g.id))
            .map((g) => ({ value: `group_${g.id}`, label: g.displayname, custom: g.id })) || [];
        return (
            subjects?.results.users
                .filter((u) => !issued.includes(u.id)).map((a) => ({ value: `user_${a.id}`, label: `${(a.hide_fio && !isAdmin) ? '' : a.firstname} ${(a.hide_fio && !isAdmin) ? '' : a.lastname} (${a.email})`, custom: a.user_id })) || []
        ).concat(groups);
    }, [subjects, myPermissions, path]);
    //#endregion
    //#region effects
    useEffect(() => {
        fetchPermissions();
    }, []);

    useEffect(() => {
        if (sdnPermissions?.results) {
            const tmp = sdnPermissions.results.reduce((acc, curr) => {
                const fname = curr.filename.slice(1);
                if (acc[fname]) {
                    acc[fname].push(curr);
                } else {
                    acc[fname] = [curr];
                }
                return acc;
            }, {} as Record<string, SdnPermission[]>);
            setMyPermissions(tmp);
        }
    }, [sdnPermissions]);
    //#endregion

    return (
        <Modal isOpen={isOpen} onClose={onClose} maxWidth={800}>
            <div className="storage__access">
                <div className="storage__access-header">
                    <div className="storage__popup-heading">
                        {path.endsWith('/') ?
                            <Trans>storage:ACCESS_SHARE_FOLDER</Trans>
                            :
                            <Trans>storage:ACCESS_SHARE_FILE</Trans>
                        }
                        {' '}
                        "{path}"
                    </div>
                    {s3dirname(path) !== '' ? <div style={{fontSize: 'smaller'}}><i><b>
                        <Trans>storage:ACCESS_DISCLAMER</Trans>
                    </b></i></div> : null}
                    <div className="storage__access-users">
                        <MultiSelect value={gUsersForSet} setValue={(v) => setGUsersForSet(v)} options={availOwnerEmails} />
                    </div>
                    <div className={`storage__access-submit ${gUsersForSet.length ? '_active' : ''}`}>
                        <div className="storage__access-submit-label">
                            <Trans>storage:ACCESS</Trans>:
                        </div>
                        <Select
                            className="storage__access-submit-access"
                            simple
                            value={permForSets}
                            onChange={(v) => setPermForSets(v)}
                            options={availPermissions.slice(0, 2)}
                        />
                        <div className="storage__access-submit-add">
                            <Button
                                disabled={gUsersForSet.length === 0}
                                onClick={() => {
                                    gUsersForSet.map((guser) => {
                                        const [gu, id] = guser.split('_');
                                        fetchCreatePermission({
                                            filename: '/' + path,
                                            group: gu === 'group' ? Number(id) : undefined,
                                            user: gu === 'user' ? id : undefined,
                                            perm: permForSets,
                                        })
                                            .unwrap()
                                            .then(() => {
                                                fetchPermissions();
                                            })
                                            .catch((e) => {
                                                alert(extractErrorDetail(e));
                                            });
                                    });
                                    setGUsersForSet([]);
                                }}
                            >
                                <Trans>Send</Trans>
                            </Button>
                        </div>
                    </div>
                </div>
                <div className={`storage__access-empty ${myPermissions[path]?.length ? '' : '_show'}`}>
                    <Trans>storage:ACCESS_USERS_NOT_SET</Trans>
                </div>
                <div className="storage__access-list">
                    {myPermissions[path]?.map((z) => (
                        <div key={z.id} className="storage__shared-data-item">
                            <div className="storage__shared-data-item-name">
                                <div className="storage__shared-data-item-name-file">{z.user ? `${(z.user.hide_fio && !isAdmin) ? '' : z.user.firstname} ${(z.user.hide_fio && !isAdmin) ? '' : z.user.lastname} (${z.user.email})` : z.group?.displayname}</div>
                                <div className="storage__shared-data-item-name-user">{z.user ? t('email') : t('group')}</div>
                                {z.reference.some((rf) => rf.ref_type === SdnRefType.ORDER) && (
                                    <div className="storage__shared-data-item-name-user">
                                        <Trans>storage:FROM_ORDER</Trans> {z.reference.filter((rf) => rf.ref_type === SdnRefType.ORDER).map((rf) => <Link className="link-accent" to={`/deals/orders?id=${rf.ref_id}`}>{rf.ref_id}</Link>)}
                                    </div>
                                )}
                            </div>
                            <Select
                                disabled={isFetching}
                                className="storage__shared-data-item-access"
                                simple
                                value={z.perm}
                                onChange={(v) => {
                                    setIsFetching(true);
                                    v === ACCESS_ACTION.DELETE ?
                                        fetchDeletePermission(z.id.toString())
                                            .unwrap()
                                            .then(() => { setIsFetching(false); })
                                            .catch((e) => { alert(extractErrorDetail(e)); })
                                        :
                                        fetchUpdatePermission({
                                            id: z.id.toString(),
                                            filename: '/' + path,
                                            group: z.group?.id,
                                            user: z.user?.id,
                                            perm: v,
                                        })
                                            .unwrap()
                                            .then(() => { setIsFetching(false); })
                                            .catch((e) => { alert(extractErrorDetail(e)); });
                                }}
                                options={availPermissions}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    )
}

export default StorageAccessModal;
